import {
  BoxGeometry,
  CanvasTexture,
  CylinderGeometry,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
  SRGBColorSpace,
} from 'three';

/**
 * A Khartoum plate in the 2009 Sudanese format: white 32 × 16 cm plate with a
 * bilingual header, the Khartoum state code (KH) and Arabic + Latin numerals.
 * This mirrors the reference layout used on real plates.
 */
const DEFAULT_PLATE = {
  country: 'SUDAN',
  countryArabic: 'السودان',
  seriesArabic: '٧٥',
  code: '7KH',
  numberArabic: '١٠٣٤٦',
  number: '1 0 3 4 6',
};

/**
 * Procedural 1990 Toyota Hilux single cab.
 *
 * The model is built from boxes to match the blocky look of the real truck —
 * and the level geometry, which uses the same visual language.
 *
 * Local space: +Z is forward, +Y is up, and the origin sits at the centre of
 * the physics collider. At rest the wheel centres sit about 0.45 m below the
 * origin and the tyre contact patches are about 0.82 m below it.
 *
 * Wheel radius/width must stay in sync with `config.vehicle.wheel`; the
 * suspension geometry that decides the resting height lives in
 * `config.vehicle.suspension`.
 */
export function createHilux({
  colors,
  wheel = { radius: 0.37, width: 0.26 },
  plate = DEFAULT_PLATE,
}) {
  const materials = {
    body: new MeshStandardMaterial({ color: colors.carBody, roughness: 0.38, metalness: 0.1 }),
    chrome: new MeshStandardMaterial({ color: colors.carChrome, roughness: 0.24, metalness: 0.85 }),
    bumper: new MeshStandardMaterial({ color: colors.carBumper, roughness: 0.38, metalness: 0.72 }),
    dark: new MeshStandardMaterial({ color: colors.carDark, roughness: 0.6, metalness: 0.25 }),
    glass: new MeshStandardMaterial({ color: colors.carGlass, roughness: 0.12, metalness: 0.55 }),
    tire: new MeshStandardMaterial({ color: colors.carTire, roughness: 0.95, metalness: 0 }),
    hub: new MeshStandardMaterial({ color: colors.carHub, roughness: 0.3, metalness: 0.8 }),
    headlight: new MeshStandardMaterial({
      color: colors.carLight,
      roughness: 0.25,
      emissive: colors.carLight,
      emissiveIntensity: 0.18,
    }),
    signal: new MeshStandardMaterial({
      color: colors.carSignal,
      roughness: 0.35,
      emissive: colors.carSignal,
      emissiveIntensity: 0.12,
    }),
    brake: new MeshStandardMaterial({
      color: colors.carBrake,
      roughness: 0.4,
      emissive: colors.carBrake,
      emissiveIntensity: 0.14,
    }),
    reverse: new MeshStandardMaterial({
      color: colors.carReverse,
      roughness: 0.4,
      emissive: colors.carReverse,
      emissiveIntensity: 0.08,
    }),
    plate: new MeshStandardMaterial({ color: 0xe9e7df, roughness: 0.6, metalness: 0.05 }),
    plateFace: new MeshStandardMaterial({
      map: createPlateTexture(plate),
      roughness: 0.45,
      metalness: 0.08,
    }),
    edge: new LineBasicMaterial({ color: colors.carSeam, transparent: true, opacity: 0.3 }),
  };

  const group = new Group();

  /**
   * Add a box-shaped panel. Everything in a Hilux is a box.
   */
  const panel = (size, position, material, { rotation = null, edges = false } = {}) => {
    const geometry = new BoxGeometry(size[0], size[1], size[2]);
    const mesh = new Mesh(geometry, material);
    mesh.position.set(position[0], position[1], position[2]);
    if (rotation) mesh.rotation.set(rotation[0], rotation[1], rotation[2]);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    if (edges) mesh.add(new LineSegments(new EdgesGeometry(geometry), materials.edge));
    group.add(mesh);
    return mesh;
  };

  /**
   * Mount a 32 × 16 cm plate on a bumper. `facing` is +1 for the nose, -1 for
   * the tail; the textured plane sits a hair proud of a thin backing box.
   */
  const addPlate = (position, facing) => {
    panel([0.32, 0.16, 0.02], position, materials.plate);

    const face = new Mesh(new PlaneGeometry(0.32, 0.16), materials.plateFace);
    face.position.set(position[0], position[1], position[2] + facing * 0.0105);
    if (facing < 0) face.rotation.y = Math.PI;
    group.add(face);
  };

  // --- Chassis and lower body -------------------------------------------
  // Frame rails run below the body, like the real ladder frame.
  panel([1.5, 0.16, 4.3], [0, -0.3, 0], materials.dark);
  // One slab forms the sills, fenders and bed lower panels.
  panel([1.7, 0.52, 4.4], [0, -0.02, 0], materials.body, { edges: true });

  // --- Front end ----------------------------------------------------------
  panel([1.62, 0.1, 1.15], [0, 0.29, 1.55], materials.body, { rotation: [0.03, 0, 0], edges: true });
  panel([1.6, 0.34, 0.1], [0, 0.14, 2.14], materials.body);
  panel([1.0, 0.2, 0.05], [0, 0.14, 2.185], materials.dark);
  panel([1.02, 0.025, 0.05], [0, 0.25, 2.19], materials.chrome);
  // Dark bezels make the lenses read against the white paint.
  panel([0.4, 0.26, 0.04], [0.58, 0.14, 2.17], materials.dark);
  panel([0.4, 0.26, 0.04], [-0.58, 0.14, 2.17], materials.dark);
  panel([0.34, 0.2, 0.05], [0.58, 0.14, 2.19], materials.headlight);
  panel([0.34, 0.2, 0.05], [-0.58, 0.14, 2.19], materials.headlight);
  panel([1.72, 0.16, 0.22], [0, 0.02, 2.26], materials.bumper);
  // Indicators live on the bumper, like the real thing.
  panel([0.22, 0.1, 0.05], [0.55, 0.02, 2.38], materials.signal);
  panel([0.22, 0.1, 0.05], [-0.55, 0.02, 2.38], materials.signal);
  addPlate([0, 0.02, 2.38], 1);

  // Chrome bull bar — mandatory desert equipment.
  panel([0.07, 0.34, 0.07], [0.5, 0.3, 2.3], materials.chrome);
  panel([0.07, 0.34, 0.07], [-0.5, 0.3, 2.3], materials.chrome);
  panel([1.24, 0.07, 0.07], [0, 0.45, 2.3], materials.chrome);

  // --- Cab ----------------------------------------------------------------
  panel([1.62, 0.66, 1.24], [0, 0.52, 0.31], materials.body, { edges: true });
  // Raked windshield; the roof lip above it reads as the classic visor.
  panel([1.46, 0.64, 0.05], [0, 0.57, 1.0], materials.glass, { rotation: [-0.42, 0, 0] });
  panel([0.05, 0.34, 1.06], [0.815, 0.63, 0.28], materials.glass);
  panel([0.05, 0.34, 1.06], [-0.815, 0.63, 0.28], materials.glass);
  panel([1.28, 0.32, 0.05], [0, 0.62, -0.33], materials.glass);
  // Chrome belt-line trim.
  panel([1.66, 0.04, 1.28], [0, 0.42, 0.31], materials.chrome);
  // Door mirrors.
  panel([0.16, 0.04, 0.04], [0.9, 0.56, 0.88], materials.dark);
  panel([0.16, 0.04, 0.04], [-0.9, 0.56, 0.88], materials.dark);
  panel([0.05, 0.14, 0.11], [0.99, 0.56, 0.88], materials.chrome);
  panel([0.05, 0.14, 0.11], [-0.99, 0.56, 0.88], materials.chrome);

  // --- Bed ----------------------------------------------------------------
  panel([0.1, 0.36, 1.72], [0.8, 0.4, -1.42], materials.body, { edges: true });
  panel([0.1, 0.36, 1.72], [-0.8, 0.4, -1.42], materials.body, { edges: true });
  panel([1.6, 0.36, 0.08], [0, 0.4, -0.58], materials.body, { edges: true });
  panel([1.68, 0.36, 0.08], [0, 0.4, -2.26], materials.body, { edges: true });
  panel([1.58, 0.05, 1.66], [0, 0.235, -1.42], materials.dark);
  for (const x of [-0.6, -0.2, 0.2, 0.6]) {
    panel([0.07, 0.025, 1.5], [x, 0.272, -1.42], materials.dark);
  }
  panel([0.12, 0.035, 1.76], [0.8, 0.6, -1.42], materials.chrome);
  panel([0.12, 0.035, 1.76], [-0.8, 0.6, -1.42], materials.chrome);

  // --- Rear end -----------------------------------------------------------
  panel([0.42, 0.3, 0.045], [0.66, 0.36, -2.3], materials.dark);
  panel([0.42, 0.3, 0.045], [-0.66, 0.36, -2.3], materials.dark);
  panel([0.2, 0.24, 0.06], [0.72, 0.36, -2.325], materials.brake);
  panel([0.2, 0.24, 0.06], [-0.72, 0.36, -2.325], materials.brake);
  panel([0.1, 0.24, 0.06], [0.56, 0.36, -2.325], materials.reverse);
  panel([0.1, 0.24, 0.06], [-0.56, 0.36, -2.325], materials.reverse);
  panel([1.74, 0.15, 0.16], [0, 0.1, -2.36], materials.bumper);
  addPlate([0, 0.1, -2.45], -1);

  const exhaust = new Mesh(
    new CylinderGeometry(0.035, 0.035, 0.26, 12),
    materials.dark,
  );
  exhaust.rotation.x = Math.PI / 2;
  exhaust.position.set(-0.55, -0.12, -2.3);
  exhaust.castShadow = true;
  group.add(exhaust);

  // --- Wheel arch flares ---------------------------------------------------
  for (const z of [1.42, -1.43]) {
    panel([0.07, 0.16, 0.94], [0.855, 0.02, z], materials.body);
    panel([0.07, 0.16, 0.94], [-0.855, 0.02, z], materials.body);
  }

  // --- Wheels ---------------------------------------------------------------
  const tireGeometry = new CylinderGeometry(wheel.radius, wheel.radius, wheel.width, 22);
  // The hub faces sit just proud of the tyre sidewall so they actually read as
  // metal wheels instead of disappearing behind the tyre's end cap.
  const hubGeometry = new CylinderGeometry(0.2, 0.2, wheel.width + 0.025, 18);
  const hubCapGeometry = new CylinderGeometry(0.065, 0.065, wheel.width + 0.045, 12);

  const createWheel = () => {
    const wheel = new Group();
    const tire = new Mesh(tireGeometry, materials.tire);
    tire.rotation.z = Math.PI / 2;
    tire.castShadow = true;
    tire.receiveShadow = true;
    wheel.add(tire);

    const hub = new Mesh(hubGeometry, materials.hub);
    hub.rotation.z = Math.PI / 2;
    hub.castShadow = true;
    wheel.add(hub);

    const hubCap = new Mesh(hubCapGeometry, materials.dark);
    hubCap.rotation.z = Math.PI / 2;
    wheel.add(hubCap);
    return wheel;
  };

  // Order: front-left, front-right, rear-left, rear-right (matches config).
  const wheels = [createWheel(), createWheel(), createWheel(), createWheel()];

  return { group, wheels, materials };
}

/**
 * Draw the plate once into a canvas and use it as a texture. Keeping it
 * procedural avoids shipping an image asset and keeps the plate crisp.
 */
function createPlateTexture(plate) {
  const width = 640;
  const height = 320;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const ink = '#101010';
  const paper = '#f5f4ee';
  const latin = '"Helvetica Neue", Helvetica, Arial, sans-serif';
  const arabic = '"Geeza Pro", "Noto Naskh Arabic", Arial, sans-serif';

  ctx.fillStyle = paper;
  ctx.fillRect(0, 0, width, height);

  // Rounded outer border.
  ctx.strokeStyle = ink;
  ctx.lineWidth = 7;
  ctx.beginPath();
  ctx.roundRect(12, 12, width - 24, height - 24, 20);
  ctx.stroke();

  // Header rule and the separator around the state block.
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(12, 88);
  ctx.lineTo(width - 12, 88);
  ctx.moveTo(234, 88);
  ctx.lineTo(234, height - 12);
  ctx.stroke();

  ctx.fillStyle = ink;
  ctx.textBaseline = 'alphabetic';

  // Header: Latin on the left, Arabic on the right.
  ctx.textAlign = 'left';
  ctx.font = `600 43px ${latin}`;
  drawTracked(ctx, plate.country, 38, 68, 7, 'left');

  ctx.textAlign = 'right';
  ctx.font = `700 46px ${arabic}`;
  ctx.fillText(plate.countryArabic, width - 38, 68);

  // State block: Arabic series above the Khartoum state code.
  ctx.textAlign = 'center';
  ctx.font = `700 78px ${arabic}`;
  ctx.fillText(plate.seriesArabic, 123, 196);

  ctx.font = `700 64px ${latin}`;
  ctx.fillText(plate.code, 123, 290);

  // Registration number: Arabic above, Latin below.
  const center = (234 + width - 12) / 2;
  ctx.font = `700 70px ${arabic}`;
  ctx.fillText(plate.numberArabic, center, 196);

  ctx.font = `600 44px ${latin}`;
  drawTracked(ctx, plate.number, center, 290, 12);

  const texture = new CanvasTexture(canvas);
  texture.colorSpace = SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
}

/** Draw text with manual tracking (ctx.letterSpacing is not universal). */
function drawTracked(ctx, text, x, baseline, tracking, align = 'center') {
  const characters = [...text];
  const widths = characters.map((character) => ctx.measureText(character).width);
  const total = widths.reduce((sum, value) => sum + value, 0) + tracking * (widths.length - 1);

  let cursor = align === 'center' ? x - total / 2 : x;
  characters.forEach((character, index) => {
    ctx.fillText(character, cursor, baseline);
    cursor += widths[index] + tracking;
  });
}
